import {
  forwardRef, type KeyboardEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import { mergeProps } from '~/_shared/utils/props/mergeProps';
import { useTheme } from '../../../themes/theme.hooks';
import { usePrevious } from '../../../utils/hooks/usePrevious';
import { isNullOrUndefined } from '../../../utils/typeGuards';
import {
  type InputProps, InputSize, TextInputComponent,
} from '../textInput';
import { InputSpinnersComponent } from './inputSpinners/inputSpinners.component';
import {
  inputStyle, suffixWrapperStyle,
} from './inputWithSpinner.styles';

export type InputWithSpinnersProps = Readonly<{
  isDisabled?: boolean;
  value?: number;
  valueMax?: number;
  valueMin?: number;
  width?: number;
  className?: string;
  hideSpinners?: boolean;
  size?: InputSize;
  placeholder?: string;
  useLocaleFormat?: boolean;

  onChange: (num: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
}>;

export const InputWithSpinnersComponent = forwardRef<HTMLInputElement, InputWithSpinnersProps>(({
  className,
  hideSpinners,
  isDisabled,
  onBlur,
  onChange,
  onFocus,
  onKeyUp,
  placeholder,
  size = InputSize.Medium,
  useLocaleFormat,
  value,
  valueMax,
  valueMin,
  width,
  ...restProps
}, ref,
) => {
  // we need to keep the value here, because users should be able to input non-final number
  // which might be outside the min-max range. e.g. they input 2 wanting to type 20 and the valueMin is 4
  const [internalValue, setInternalValue] = useState(value);
  const [hasFocus, setHasFocus] = useState(false);
  const previousPropsValue = usePrevious(value);
  const theme = useTheme();

  const applyMinMaxBoundariesToValue = useCallback((value: string | number): number | null => {
    const maxValue = valueMax ?? Number.MAX_VALUE;
    const minValue = valueMin ?? Number.MIN_VALUE;
    let newNumber = Number(value);

    if (!isNaN(newNumber)) {
      if (newNumber > maxValue) {
        newNumber = maxValue;
      }
      if (newNumber < minValue) {
        newNumber = minValue;
      }
      return newNumber;
    }
    return null;
  }, [valueMax, valueMin]);

  const onNewValue = useCallback((value: string | number): void => {
    const newValue = Number(value);

    if (isNaN(newValue)) {
      setInternalValue(prevValue => prevValue ?? 0);
      return;
    }

    if (value === '') {
      setInternalValue(undefined);
      return;
    }

    const newNumber = applyMinMaxBoundariesToValue(value);
    if (newNumber !== null && newNumber === Number(value)) {
      onChange(newNumber);
    }
    setInternalValue(newValue);
  }, [applyMinMaxBoundariesToValue, onChange]);

  const isInternalValueValid = useMemo(() => (
    (isNullOrUndefined(internalValue) && placeholder) ||
    (!isNullOrUndefined(internalValue) && applyMinMaxBoundariesToValue(internalValue ?? 0) === internalValue)
  ), [applyMinMaxBoundariesToValue, internalValue, placeholder]);

  const handleBlur = useCallback(() => {
    // if user moved away from the input without finishing typing in a valid value, use default
    if (!isInternalValueValid) {
      setInternalValue(value);
    }

    onBlur?.();

    setHasFocus(false);
  }, [isInternalValueValid, onBlur, value]);

  const handleFocus = useCallback(() => {
    onFocus?.();

    setHasFocus(true);
  }, [onFocus]);

  const inputProps: InputProps = useMemo(() => ({
    onChange: (value: string) => onNewValue(value),
    value: (useLocaleFormat && !hasFocus) ? internalValue?.toLocaleString() : internalValue?.toString() ?? '',
    isDisabled,
    onBlur: handleBlur,
    onFocus: handleFocus,
    size,
    icon: null,
    onKeyUp,
  }), [useLocaleFormat, hasFocus, internalValue, isDisabled, handleBlur, handleFocus, size, onKeyUp, onNewValue]);

  useEffect(() => {
    if (previousPropsValue !== value) {
      setInternalValue(value);
    }
  }, [previousPropsValue, value]);

  return (
    <TextInputComponent
      ref={ref}
      {...mergeProps(inputProps, restProps)}
      className={className}
      placeholder={placeholder}
      css={inputStyle({
        theme,
        width,
        hasError: !isInternalValueValid,
      })}
      rightContent={hideSpinners ? null : (
        <span css={suffixWrapperStyle({ theme, inputSize: size })}>
          <InputSpinnersComponent
            min={valueMin}
            max={valueMax}
            isDisabled={isDisabled}
            value={internalValue ?? 0}
            valueSetter={(value: number) => onNewValue(value)}
          />
        </span>
      )}
    />
  );
});
